import api from "../../../api";
import {param, typedQuery} from "../../typing";
import {
  AttributeAssessmentId,
  ComplexityModelId,
  ComplexityModelVersionId,
  ComplexityAssessmentId,
  EngagementId,
  RollForwardMode,
  ThirdPartyId,
  TprmScopeId,
} from "platformed-client/types";

export const complexityModels = typedQuery(["tprm", "complexityModels"], async () => {
  return await api.tprm.complexity.models.list();
});

export const complexityModel = typedQuery(
  ["tprm", "complexityModel", param<ComplexityModelId>("complexityModelId")],
  async modelId => {
    return await api.tprm.complexity.models.get_(modelId);
  },
);

export const complexityModelVersions = typedQuery(
  ["tprm", "complexityModelVersions", param<ComplexityModelId>("complexityModelId")],
  async modelId => {
    return await api.tprm.complexity.modelVersions.list(modelId);
  },
);

export const complexityModelVersion = typedQuery(
  [
    "tprm",
    "complexityModelVersion",
    param<ComplexityModelId>("complexityModelId"),
    param<ComplexityModelVersionId>("complexityModelVersionId"),
  ],
  async (modelId, versionId) => {
    return await api.tprm.complexity.modelVersions.get_(modelId, versionId);
  },
);

export const complexityModelVersionExport = typedQuery(
  [
    "tprm",
    "complexityModelVersionExport",
    param<ComplexityModelId>("complexityModelId"),
    param<ComplexityModelVersionId>("complexityModelVersionId"),
  ],
  async (modelId, versionId) => {
    return await api.tprm.complexity.modelVersions.exportAsPreset(modelId, versionId);
  },
);

export const complexityTiers = typedQuery(
  [
    "tprm",
    "complexityTier",
    param<ComplexityModelId>("complexityModelId"),
    param<ComplexityModelVersionId>("complexityModelVersionId"),
  ],
  async (modelId, versionId) => {
    return await api.tprm.complexity.tiers.list(modelId, versionId);
  },
);

export const complexityTiersOnAccount = typedQuery(["tprm", "complexityTier"], async () => {
  return await api.tprm.complexity.tiers.listAll();
});

export const usedComplexityTiersOnAccount = typedQuery(["tprm", "usedComplexityTier"], async () => {
  return await api.tprm.complexity.tiers.listUsed();
});

export const complexityTierAutoruns = typedQuery(
  [
    "tprm",
    "complexityTierAutoruns",
    param<ComplexityModelId>("complexityModelId"),
    param<ComplexityModelVersionId>("complexityModelVersionId"),
  ],
  async (modelId, versionId) => {
    return await api.tprm.complexity.tierAutoruns.list(modelId, versionId);
  },
);

export const thirdPartyComplexityAssessments = typedQuery(
  ["tprm", "thirdPartyComplexityAssessments", param<ThirdPartyId>("thirdPartyId")],
  async thirdPartyId => {
    return await api.tprm.complexity.assessments.search({third_party_id: thirdPartyId});
  },
);

export const engagementComplexityAssessments = typedQuery(
  ["tprm", "engagementComplexityAssessments", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.complexity.assessments.search({engagement_id: engagementId});
  },
);

/** Parent (batch-level) complexity assessments for an engagement. Companion to `engagementComplexityAssessments`, which lists per-scope members across all scope types. */
export const engagementComplexityAssessmentBatches = typedQuery(
  ["tprm", "engagementComplexityAssessmentBatches", param<EngagementId>("engagementId")],
  async engagementId => {
    return await api.tprm.complexity.assessments.searchBatches({engagement_id: engagementId});
  },
);

export const complexityAssessment = typedQuery(
  ["tprm", "complexityAssessment", param<ComplexityAssessmentId>("complexityAssessmentId")],
  async assessmentId => {
    return await api.tprm.complexity.assessments.getById(assessmentId);
  },
);

/** Per-scope members of a complexity assessment batch (one row per member). */
export const complexityAssessmentScopes = typedQuery(
  ["tprm", "complexityAssessmentScopes", param<ComplexityAssessmentId>("complexityAssessmentId")],
  async assessmentId => {
    return await api.tprm.complexity.assessments.listScopes(assessmentId);
  },
);

/** Full complexity assessment for one batch member, addressed by (id, scope_id). */
export const complexityAssessmentMember = typedQuery(
  [
    "tprm",
    "complexityAssessmentMember",
    param<ComplexityAssessmentId>("complexityAssessmentId"),
    param<TprmScopeId>("scopeId"),
  ],
  async (assessmentId, scopeId) => {
    return await api.tprm.complexity.assessments.getMember(assessmentId, scopeId);
  },
);

/** Null before the member has ever been rolled forward. */
export const complexityRollForward = typedQuery(
  [
    "tprm",
    "complexityRollForward",
    param<ComplexityAssessmentId>("complexityAssessmentId"),
    param<TprmScopeId>("scopeId"),
  ],
  async (assessmentId, scopeId) => {
    return await api.tprm.complexity.assessments.getRollForward(assessmentId, scopeId);
  },
);

/** What a run from the given source in the given mode would do; read-only, so a query rather than a mutation. */
export const complexityRollForwardPlan = typedQuery(
  [
    "tprm",
    "complexityRollForwardPlan",
    param<ComplexityAssessmentId>("complexityAssessmentId"),
    param<TprmScopeId>("scopeId"),
    param<ComplexityAssessmentId>("sourceComplexityAssessmentId"),
    param<TprmScopeId>("sourceScopeId"),
    param<RollForwardMode>("mode"),
  ],
  async (assessmentId, scopeId, sourceAssessmentId, sourceScopeId, mode) => {
    return await api.tprm.complexity.assessments.planRollForward(assessmentId, scopeId, {
      source: {complexity_assessment_id: sourceAssessmentId, scope_id: sourceScopeId},
      mode,
    });
  },
);

export const complexityRollForwardCandidates = typedQuery(
  [
    "tprm",
    "complexityRollForwardCandidates",
    param<ComplexityAssessmentId>("complexityAssessmentId"),
    param<TprmScopeId>("scopeId"),
  ],
  async (assessmentId, scopeId) => {
    return await api.tprm.complexity.assessments.listRollForwardCandidates(assessmentId, scopeId);
  },
);

export const attributeCitations = typedQuery(
  [
    "tprm",
    "attributeCitations",
    param<ComplexityAssessmentId>("complexityAssessmentId"),
    param<AttributeAssessmentId>("attributeAssessmentId"),
  ],
  async (complexityAssessmentId, attributeAssessmentId) => {
    return await api.tprm.complexity.attributeCitations.list(complexityAssessmentId, attributeAssessmentId);
  },
);
